import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function AwarenessVideosManager() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtubeUrl: '',
    thumbnailUrl: '',
    duration: '',
    category: 'Education',
    order: 1,
  });

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const q = query(collection(db, 'awarenessVideos'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const videosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVideos(videosData);
    } catch (error) {
      console.error('Error loading awareness videos:', error);
      alert('Failed to load awareness videos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingVideo) {
        await updateDoc(doc(db, 'awarenessVideos', editingVideo.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Video updated successfully!');
      } else {
        await addDoc(collection(db, 'awarenessVideos'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Video added successfully!');
      }
      setShowModal(false);
      setEditingVideo(null);
      resetForm();
      loadVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Failed to save video');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description,
      youtubeUrl: video.youtubeUrl,
      thumbnailUrl: video.thumbnailUrl,
      duration: video.duration || '',
      category: video.category || 'Education',
      order: video.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await deleteDoc(doc(db, 'awarenessVideos', id));
      alert('Video deleted successfully!');
      loadVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Failed to delete video');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      youtubeUrl: '',
      thumbnailUrl: '',
      duration: '',
      category: 'Education',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Awareness Videos Management</h2>
            <button
              onClick={() => {
                setEditingVideo(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Video
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && videos.length === 0 && (
            <p>No awareness videos found. Add your first video!</p>
          )}

          {!loading && videos.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Thumbnail</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <tr key={video.id}>
                    <td>{video.order}</td>
                    <td>
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        style={{ width: '120px', height: '68px', objectFit: 'cover', borderRadius: '4px' }}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/120x68?text=No+Thumbnail'; }}
                      />
                    </td>
                    <td style={{ maxWidth: '200px' }}>{video.title}</td>
                    <td style={{ maxWidth: '250px', fontSize: '12px' }}>{video.description}</td>
                    <td>
                      <span className="badge badge-primary">{video.category || 'Education'}</span>
                    </td>
                    <td>{video.duration || '-'}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(video)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(video.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="info-box">
          <h3>Video Guidelines</h3>
          <p><strong>YouTube URL:</strong> Full YouTube video URL (e.g., https://www.youtube.com/watch?v=xxxxx)</p>
          <p><strong>Thumbnail URL:</strong> Direct image URL for video thumbnail (16:9 aspect ratio recommended)</p>
          <p><strong>Duration:</strong> Video length in format like "5:23" or "12:45"</p>
          <p><strong>Category:</strong> Classification like Education, Awareness, Rights, Safety, etc.</p>
          <p><strong>Tip:</strong> Get YouTube thumbnail using: https://img.youtube.com/vi/VIDEO_ID/maxresdefault.jpg</p>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingVideo ? 'Edit Awareness Video' : 'Add New Awareness Video'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Save the Girl Child - Awareness Campaign"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the video content"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>YouTube URL *</label>
                <input
                  type="url"
                  value={formData.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=xxxxx"
                  required
                />
              </div>

              <div className="form-group">
                <label>Thumbnail URL *</label>
                <input
                  type="url"
                  value={formData.thumbnailUrl}
                  onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                  placeholder="https://img.youtube.com/vi/VIDEO_ID/maxresdefault.jpg"
                  required
                />
                {formData.thumbnailUrl && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={formData.thumbnailUrl}
                      alt="Thumbnail Preview"
                      style={{ width: '100%', maxWidth: '400px', borderRadius: '8px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Duration (Optional)</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 5:23"
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="Education">Education</option>
                  <option value="Awareness">Awareness</option>
                  <option value="Rights">Rights</option>
                  <option value="Safety">Safety</option>
                  <option value="Empowerment">Empowerment</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Success Stories">Success Stories</option>
                </select>
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingVideo ? 'Update Video' : 'Add Video'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingVideo(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AwarenessVideosManager;
